<template>
  <div class="alice-shell theme-turquoise">
    <TopBar class="alice-shell__topbar">
      <template #breadcrumb>
        <span class="alice-tb-crumb alice-tb-crumb--current">Skills</span>
      </template>
    </TopBar>

    <Sidebar class="alice-shell__sidebar" variant="rail" active-key="skills" />

    <main class="alice-shell__main">
      <PageHeader
        title="Skills"
        :count="entries.length"
        subtitle="Ways of working that Alice, Claude and ChatGPT follow step by step. Open one to read it, change it or review a new version."
      />

      <!-- Toolbar: same idiom as the Previews and Changelog toolbars, with the
           filter chips in it so they stay in reach while scrolling -->
      <div class="sk-toolbar alice-shell__toolbar">
        <label class="sk-toolbar__search">
          <span class="sk-sr">Filter the skills</span>
          <Icon name="search" :size="16" />
          <input v-model="q" type="text" placeholder="Search skills…" />
          <span v-if="!loading" class="sk-toolbar__count"
            >{{ shownCount }} {{ shownCount === 1 ? "skill" : "skills" }}</span
          >
        </label>
        <div
          v-if="!loading && entries.length"
          class="sk-filters"
          role="group"
          aria-label="Filter"
        >
          <button
            v-if="chips.waiting"
            type="button"
            class="sk-filter sk-filter--waiting"
            :class="{ 'sk-filter--on': waiting }"
            :aria-pressed="waiting"
            @click="setFilter({ waiting: true })"
          >
            Waiting for you
            <span class="sk-filter__count">{{ chips.waiting }}</span>
          </button>
          <button
            v-if="chips.feedback"
            type="button"
            class="sk-filter sk-filter--feedback"
            :class="{ 'sk-filter--on': feedback }"
            :aria-pressed="feedback"
            title="Skills you own or approve with open feedback from colleagues"
            @click="setFilter({ feedback: true })"
          >
            Feedback for you
            <span class="sk-filter__count">{{ chips.feedback }}</span>
          </button>
          <button
            type="button"
            class="sk-filter"
            :class="{
              'sk-filter--on': !category && !mine && !waiting && !feedback,
            }"
            :aria-pressed="!category && !mine && !waiting && !feedback"
            @click="setFilter()"
          >
            All <span class="sk-filter__count">{{ chips.all }}</span>
          </button>
          <button
            v-for="c in chips.categories"
            :key="c.key"
            type="button"
            class="sk-filter"
            :class="{ 'sk-filter--on': category === c.key }"
            :aria-pressed="category === c.key"
            @click="setFilter({ category: c.key })"
          >
            <span
              class="sk-swatch"
              :style="{ background: rampColor(c.label) }"
            />
            {{ c.label }}
            <span class="sk-filter__count">{{ c.count }}</span>
          </button>
          <button
            v-if="chips.mine"
            type="button"
            class="sk-filter"
            :class="{ 'sk-filter--on': mine }"
            :aria-pressed="mine"
            @click="setFilter({ mine: true })"
          >
            Mine <span class="sk-filter__count">{{ chips.mine }}</span>
          </button>
        </div>
        <div v-if="!isGuest" class="sk-toolbar__actions">
          <router-link
            class="sk-toolbar__ghost"
            :to="{ path: '/skills/new', query: { ai: '1' } }"
          >
            <Icon name="star" :size="14" /><span>Write with AI</span>
          </router-link>
          <router-link class="sk-toolbar__new" to="/skills/new">
            <Icon name="plus" :size="14" /><span>New skill</span>
          </router-link>
        </div>
      </div>

      <div class="alice-shell__list sk-list">
        <div v-if="loading" class="sk-empty">
          <v-progress-circular indeterminate size="28" width="3" />
        </div>

        <template v-else>
          <div v-if="!entries.length" class="sk-empty">
            No skills yet. Create one or write one with AI.
          </div>
          <div v-else-if="!sections.length" class="sk-empty">
            No skill matches this search or filter.
          </div>

          <section
            v-for="s in sections"
            :key="s.key"
            class="sk-section"
            :aria-label="s.label"
          >
            <h2 class="sk-section__label">
              <span
                class="sk-swatch"
                :style="{ background: `var(--vp-${s.ramp}-100)` }"
              />
              {{ s.label }}
              <span class="sk-section__count">{{ s.skills.length }}</span>
            </h2>
            <div class="sk-grid">
              <router-link
                v-for="e in s.skills"
                :key="e.id"
                :to="cardLink(e)"
                class="sk-card"
                :class="{
                  'sk-card--unused': e.neverUsed,
                  'sk-card--attention': e.state && e.state.kind === 'review',
                }"
              >
                <span
                  class="sk-card__stripe"
                  :style="{ background: `var(--vp-${s.ramp}-100)` }"
                />
                <span class="sk-card__body">
                  <span class="sk-card__chips">
                    <span v-if="e.live" class="sk-state sk-state--live"
                      >Live</span
                    >
                    <span
                      v-if="e.state"
                      class="sk-state"
                      :class="`sk-state--${e.state.kind}`"
                      >{{ e.state.label }}</span
                    >
                    <span
                      v-if="e.feedbackOpen"
                      class="sk-state sk-state--feedback"
                      :title="`${e.feedbackOpen} open feedback ${
                        e.feedbackOpen === 1 ? 'item' : 'items'
                      } from colleagues who used this skill`"
                      >{{ e.feedbackOpen }} feedback</span
                    >
                  </span>
                  <h3 class="sk-card__title" :title="e.title">
                    {{ e.title }}
                  </h3>
                  <span v-if="e.summary" class="sk-card__summary">{{
                    e.summary
                  }}</span>
                  <span class="sk-card__foot">
                    <span class="sk-card__who">
                      <span class="sk-card__owner">{{
                        e.ownerName || "Owner unknown"
                      }}</span>
                      <span class="sk-card__meta"
                        >v{{ e.version
                        }}<template v-if="e.updatedAt">
                          · updated {{ shortDate(e.updatedAt) }}</template
                        ></span
                      >
                    </span>
                    <span
                      v-if="e.neverUsed"
                      class="sk-card__uses sk-card__uses--none"
                      title="No use counted yet in chat, team chat or MCP"
                      >Not used yet</span
                    >
                    <span
                      v-else-if="usageLoaded"
                      class="sk-card__uses"
                      :title="`${e.uses30} ${
                        e.uses30 === 1 ? 'use' : 'uses'
                      } in the last 30 days`"
                      ><strong>{{ e.uses30 }}×</strong>
                      <span>30 days</span></span
                    >
                  </span>
                </span>
              </router-link>
            </div>
          </section>

          <p v-if="sections.length && usageLoaded" class="sk-foot">
            Uses count the last 30 days: each answer the skill was active in,
            plus each fetch by an MCP client.
          </p>
        </template>
      </div>
    </main>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "@/firebase";

import TopBar from "@/components/alice/TopBar.vue";
import Sidebar from "@/components/alice/Sidebar.vue";
import PageHeader from "@/components/alice/PageHeader.vue";
import Icon from "@/components/alice/Icon.vue";
import {
  canApproveSkill,
  categoryChips,
  categoryRamp,
  groupSkills,
  isLive,
  shownVersion,
  skillCardState,
  skillSummary,
  windowStart,
} from "@/utils/skillLibrary";

// Each card's 30-day count is the sum of that skill's dailyUses buckets (the
// same query the kennisbank runs); a missing or unreadable bucket counts 0.
async function usesSince(skillId, since) {
  try {
    const snap = await getDocs(
      query(
        collection(db, "skillUsage", skillId, "dailyUses"),
        where("date", ">=", since)
      )
    );
    return snap.docs.reduce((n, d) => n + (d.data().uses || 0), 0);
  } catch (e) {
    return 0;
  }
}

export default {
  name: "SkillLibraryPage",
  components: { TopBar, Sidebar, PageHeader, Icon },
  data() {
    return {
      loading: true,
      q: "",
      // Category key (lower case) or "" for all.
      category: "",
      mine: false,
      waiting: false,
      feedback: false,
      approvers: [],
      metaById: {},
      // All-time counters (skillUsage/{id}) and the 30-day sums per skill.
      usageById: {},
      uses30ById: {},
      usageLoaded: false,
    };
  },
  computed: {
    ...mapState({
      skills: (state) => state.skills,
      user: (state) => state.user,
      users: (state) => state.users || [],
    }),
    isGuest() {
      return this.$store.getters.isGuest;
    },
    isMax() {
      return !!(this.user && this.user.email === "max@braingineers.com");
    },
    approveCtx() {
      return {
        uid: this.user && this.user.uid,
        isMax: this.isMax,
        approvers: this.approvers,
      };
    },
    entries() {
      return (this.skills || []).map((s) => {
        const canApprove = canApproveSkill(s, this.approveCtx);
        const usage = this.usageById[s.id];
        return {
          id: s.id,
          name: s.name,
          title: s.title || s.name || "Untitled skill",
          category: s.category,
          summary: skillSummary(s, this.metaById[s.id]),
          owner: s.owner || null,
          createdBy: s.createdBy || null,
          ownerName: this.userName(s.owner),
          version: shownVersion(s),
          updatedAt: s.updatedAt || null,
          live: isLive(s),
          state: skillCardState(s, { canApprove }),
          canApprove,
          feedbackOpen: s.feedbackOpen || 0,
          uses30: this.uses30ById[s.id] || 0,
          // Dashed "try it" card: live, and not a single use counted ever.
          neverUsed: this.usageLoaded && isLive(s) && !(usage && usage.uses),
        };
      });
    },
    chips() {
      return categoryChips(this.entries, { uid: this.user && this.user.uid });
    },
    sections() {
      return groupSkills(this.entries, {
        query: this.q,
        category: this.category,
        mine: this.mine,
        waiting: this.waiting,
        feedback: this.feedback,
        uid: this.user && this.user.uid,
      });
    },
    shownCount() {
      return this.sections.reduce((n, s) => n + s.skills.length, 0);
    },
  },
  watch: {
    // A hard reload mounts this page before Firebase Auth restores the
    // session, when every read would be refused: load once the user is known.
    user(u, prev) {
      if (u && !prev) this.load();
    },
  },
  mounted() {
    if (this.user) this.load();
  },
  methods: {
    // Refetch: a colleague may have saved or approved something since this
    // tab loaded the list. Cards wait for the extras too, so they don't
    // reshuffle once the 30-day counts (their sort order) arrive.
    async load() {
      this.loading = true;
      try {
        await this.$store.dispatch("fetchSkills");
        await this.loadExtras();
      } finally {
        this.loading = false;
      }
    },
    setFilter({
      category = "",
      mine = false,
      waiting = false,
      feedback = false,
    } = {}) {
      this.category = category;
      this.mine = mine;
      this.waiting = waiting;
      this.feedback = feedback;
    },
    // With "Feedback for you" on, a card opens straight on its Feedback tab.
    cardLink(e) {
      return this.feedback && e.canApprove && e.feedbackOpen
        ? { path: `/skills/${e.id}`, query: { tab: "feedback" } }
        : `/skills/${e.id}`;
    },
    rampColor(category) {
      return `var(--vp-${categoryRamp(category)}-100)`;
    },
    userName(uid) {
      if (!uid) return null;
      const u = this.users.find((x) => x.uid === uid);
      return (u && (u.name || u.email)) || null;
    },
    // Same format as the Previews cards ("21 okt 2026").
    shortDate(ts) {
      if (!ts) return "—";
      return new Date(ts).toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    // Approvers, curated summaries and usage are extras: each fails on its
    // own (guests can't read most of them) and the cards still render.
    async loadExtras() {
      const ids = (this.skills || []).map((s) => s.id);
      const since = windowStart(30);
      const [approvers, meta, usage, uses30] = await Promise.allSettled([
        getDoc(doc(db, "admin", "skillApprovers")),
        getDocs(collection(db, "kb_skill_meta")),
        getDocs(collection(db, "skillUsage")),
        Promise.all(ids.map((id) => usesSince(id, since))),
      ]);
      if (approvers.status === "fulfilled" && approvers.value.exists()) {
        const uids = approvers.value.data().uids;
        this.approvers = Array.isArray(uids) ? uids : [];
      }
      if (meta.status === "fulfilled") {
        this.metaById = Object.fromEntries(
          meta.value.docs.map((d) => [d.id, d.data()])
        );
      }
      if (usage.status === "fulfilled") {
        this.usageById = Object.fromEntries(
          usage.value.docs.map((d) => [d.id, d.data()])
        );
      }
      if (uses30.status === "fulfilled") {
        this.uses30ById = Object.fromEntries(
          ids.map((id, i) => [id, uses30.value[i]])
        );
      }
      this.usageLoaded =
        usage.status === "fulfilled" && uses30.status === "fulfilled";
    },
  },
};
</script>

<style scoped lang="scss">
/* Shell: same grid as the Previews and Changelog pages */
.alice-shell {
  display: grid;
  grid-template-rows: 56px 1fr;
  grid-template-columns: 56px 1fr;
  height: 100vh;
  width: 100%;
  background: var(--bg-muted);
  font-family: var(--font-body);
  color: var(--fg-1);
  overflow: hidden;
}
.alice-shell__topbar {
  grid-column: 1 / -1;
  grid-row: 1;
}
.alice-shell__sidebar {
  grid-column: 1;
  grid-row: 2;
}
/* The header scrolls away with the list; only the toolbar stays in view. */
.alice-shell__main {
  grid-column: 2;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-y: auto;
}
.alice-shell__main > * {
  flex-shrink: 0;
}
.alice-shell__toolbar {
  position: sticky;
  top: 0;
  z-index: 3;
}
.alice-shell__list {
  flex-grow: 1;
  background: var(--vp-white);
}
.alice-tb-crumb--current {
  color: var(--vp-white);
}
.sk-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* ===== toolbar (same idiom as the Previews toolbar) =====
   One row: search · filter chips · actions. Where the chips don't fit next
   to the search, they drop to a row of their own. */
.sk-toolbar {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) auto auto;
  grid-template-areas: "search filters actions";
  align-items: center;
  gap: 10px 16px;
  padding: 12px 32px;
  background: var(--vp-white);
  border-bottom: 1px solid var(--border-hairline);
}
.sk-toolbar__search {
  grid-area: search;
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  color: var(--fg-3);
}
.sk-toolbar__search input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--fg-1);
  background: transparent;
}
.sk-toolbar__count {
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  color: var(--fg-3);
  white-space: nowrap;
}
.sk-toolbar__actions {
  grid-area: actions;
  display: flex;
  align-items: center;
  gap: 8px;
}
.sk-toolbar__new,
.sk-toolbar__ghost {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  border: none;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.sk-toolbar__new {
  background: var(--vp-black);
  color: var(--vp-white);
}
.sk-toolbar__new:hover {
  background: var(--accent-100);
  color: var(--vp-black);
}
.sk-toolbar__ghost {
  background: var(--vp-white);
  color: var(--vp-black);
  box-shadow: inset 0 0 0 1px var(--border-hairline);
}
.sk-toolbar__ghost:hover {
  background: var(--bg-muted);
  color: var(--vp-black);
}

/* ===== list ===== */
.sk-list {
  padding: 24px 32px 40px;
}
.sk-empty {
  padding: 60px 0;
  text-align: center;
  color: var(--fg-3);
  font-family: var(--font-ui);
  font-size: 14px;
}

/* Filter chips (in the toolbar) */
.sk-filters {
  grid-area: filters;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.sk-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  color: var(--fg-2);
}
.sk-filter:hover {
  background: var(--bg-muted);
  color: var(--vp-black);
}
/* Same yellow as the cards that wait for your review */
.sk-filter--waiting {
  background: var(--vp-geel-10);
  border-color: var(--vp-geel-100);
  color: var(--vp-geel-160);
}
.sk-filter--waiting:hover {
  background: var(--vp-geel-40);
  color: var(--vp-black);
}
.sk-filter--feedback {
  background: var(--vp-blauw-10);
  border-color: var(--vp-blauw-100);
  color: var(--vp-blauw-160);
}
.sk-filter--feedback:hover {
  background: var(--vp-blauw-40);
  color: var(--vp-black);
}
.sk-filter--on,
.sk-filter--on:hover {
  background: var(--vp-black);
  border-color: var(--vp-black);
  color: var(--vp-white);
}
.sk-filter__count {
  font-weight: 400;
  opacity: 0.75;
}
.sk-swatch {
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
}

/* Sections: labelled like the Changelog's month groups */
.sk-section {
  padding-top: 20px;
}
.sk-section:first-of-type {
  padding-top: 0;
}
.sk-section__label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.sk-section__count {
  font-weight: 400;
  letter-spacing: 0;
}

/* ===== cards (same anatomy as the Previews / Projects cards) ===== */
.sk-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(340px, 100%), 1fr));
  gap: 20px;
}
.sk-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  color: inherit;
  text-decoration: none;
  outline: 2px solid transparent;
  outline-offset: -2px;
  transition: outline-color var(--dur-fast) var(--ease-hover);
}
.sk-card:hover,
.sk-card:focus-visible {
  outline-color: var(--accent-100);
  color: inherit;
}
.sk-card--unused {
  border-style: dashed;
  border-color: var(--vp-grijs-60);
}
.sk-card--attention {
  background: var(--vp-geel-10);
  border-color: var(--vp-geel-100);
}
.sk-card__stripe {
  height: 6px;
  flex-shrink: 0;
}
.sk-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 16px 12px;
  min-width: 0;
}
.sk-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 20px;
}
.sk-card__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.01em;
  color: var(--vp-black);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}
.sk-card__summary {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 14px;
  line-height: 1.45;
  color: var(--fg-2);
}
.sk-card__foot {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid var(--border-hairline);
}
.sk-card__who {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.sk-card__owner {
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  color: var(--vp-black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sk-card__meta {
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--fg-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sk-card__uses {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--fg-3);
  white-space: nowrap;
}
.sk-card__uses strong {
  font-size: 14px;
  color: var(--vp-black);
}
.sk-card__uses--none {
  font-weight: 700;
  color: var(--vp-blauw-140);
}

/* State chips: the Previews chip shape, the skill editor's colours */
.sk-state {
  flex-shrink: 0;
  padding: 2px 8px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: var(--bg-muted);
  color: var(--fg-3);
}
.sk-state--live {
  background: var(--accent-20);
  color: var(--vp-black);
}
.sk-state--in_review,
.sk-state--review {
  background: #fff4d6;
  color: #7a5b00;
}
.sk-state--review {
  box-shadow: inset 0 0 0 1px var(--vp-geel-100);
}
.sk-state--changes_requested {
  background: #fdecec;
  color: #c62828;
}
.sk-state--feedback {
  background: var(--vp-blauw-10);
  color: var(--vp-blauw-160);
}

.sk-foot {
  margin: 24px 0 0;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}

@media (max-width: 1599px) {
  .sk-toolbar {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "search actions"
      "filters filters";
  }
}
@media (max-width: 720px) {
  .sk-toolbar {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "search"
      "actions"
      "filters";
    padding: 12px 16px;
  }
  /* One swipeable row, so the sticky toolbar stays short on a phone */
  .sk-filters {
    flex-wrap: nowrap;
    overflow-x: auto;
  }
  .sk-filter {
    flex-shrink: 0;
  }
  .sk-list {
    padding: 16px 16px 32px;
  }
}
</style>
